import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search, Filter, Grid, List, ArrowRight, BookOpen, Sparkles } from 'lucide-react';
import SEOHead from '../components/SEOHead';
import Breadcrumbs from '../components/Breadcrumbs';
import {
  glossaryIndex,
  glossaryCategories,
  searchTerms,
  getTermsByCategory,
  getAlphabetIndex
} from '../data/glossaryIndex';

const GlossaryPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [viewMode, setViewMode] = useState('grid'); // grid or list
  const [filteredTerms, setFilteredTerms] = useState(glossaryIndex);
  const [selectedLetter, setSelectedLetter] = useState('');

  useEffect(() => {
    let terms = glossaryIndex;
    
    // Apply category filter
    if (selectedCategory !== 'All') {
      terms = getTermsByCategory(selectedCategory);
    }
    
    // Apply search filter
    if (searchQuery) {
      terms = searchTerms(searchQuery);
    }
    
    // Apply letter filter
    if (selectedLetter) {
      terms = terms.filter(term => 
        term.term.charAt(0).toLowerCase() === selectedLetter.toLowerCase()
      );
    }
    
    setFilteredTerms(terms);
  }, [searchQuery, selectedCategory, selectedLetter]);

  const alphabetIndex = getAlphabetIndex();

  const breadcrumbItems = [
    { label: 'Glossary', href: null }
  ];

  const glossarySetSchema = {
    "@context": "https://schema.org",
    "@type": "DefinedTermSet",
    "name": "Sortio File Organization Glossary",
    "description": "Comprehensive glossary of file organization, document management, and AI-powered sorting terms. Learn everything about digital file management with Sortio's comprehensive guide.",
    "url": "https://www.getsortio.com/glossary",
    "publisher": {
      "@type": "Organization",
      "name": "Sortio",
      "url": "https://www.getsortio.com"
    },
    "numberOfItems": glossaryIndex.length,
    "hasPart": glossaryIndex.map(term => ({
      "@type": "DefinedTerm",
      "name": term.term,
      "description": term.shortDescription,
      "url": `https://www.getsortio.com/glossary/${term.slug}`
    }))
  };

  const handleLetterClick = (letter) => {
    if (selectedLetter === letter) {
      setSelectedLetter('');
    } else {
      setSelectedLetter(letter);
    }
    setSearchQuery('');
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedCategory('All');
    setSelectedLetter('');
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#121212" }}>
      <SEOHead 
        title="File Organization Glossary | Sortio - AI File Management Terms"
        description="Complete glossary of file organization, document management, and AI-powered sorting terms. Learn everything about digital file management with Sortio's comprehensive guide."
        canonical="https://www.getsortio.com/glossary"
        schema={glossarySetSchema}
        keywords="file organization glossary, document management terms, AI file sorting definitions, digital filing terminology, file management dictionary"
      />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Breadcrumbs items={breadcrumbItems} />
        
        {/* Header Section */}
        <div className="text-center mb-16">
          <div className="inline-block mb-4">
            <div className="flex items-center justify-center p-1.5 rounded-full bg-sortio-secondary/10 px-4 py-1.5">
              <BookOpen className="mr-2 text-sortio-secondary" size={14} />
              <span className="text-xs font-medium uppercase tracking-wider text-sortio-secondary">Knowledge Base</span>
            </div>
          </div>
          <h1 className="text-5xl md:text-6xl font-semibold mb-6 tracking-tight text-apple-50">
            File Organization <span className="apple-gradient-text">Glossary</span>
          </h1>
          <p className="text-apple-300 text-xl max-w-3xl mx-auto font-light leading-relaxed">
            Master file organization with our comprehensive glossary of AI-powered document management, digital filing systems, and productivity tools.
          </p>
          <div className="mt-8 text-apple-400">
            <span className="font-medium">{filteredTerms.length}</span> terms available
          </div>
        </div>

        {/* Search and Filter Controls */}
        <div className="mb-12 space-y-6">
          {/* Search Bar */}
          <div className="relative max-w-2xl mx-auto">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-apple-400" size={20} />
            <input
              type="text"
              placeholder="Search glossary terms..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-4 bg-apple-700/40 border border-apple-600/30 rounded-2xl text-apple-50 placeholder-apple-400 focus:outline-none focus:ring-2 focus:ring-sortio-primary/50 focus:border-sortio-primary/50 transition-all duration-300"
            />
          </div>

          {/* Category Filter and View Toggle */}
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <Filter className="text-apple-400" size={20} />
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="bg-apple-700/40 border border-apple-600/30 rounded-xl px-4 py-2 text-apple-50 focus:outline-none focus:ring-2 focus:ring-sortio-primary/50"
              >
                {glossaryCategories.map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
              {(searchQuery || selectedCategory !== 'All' || selectedLetter) && (
                <button
                  onClick={clearFilters}
                  className="text-sortio-primary hover:text-sortio-primary/80 text-sm font-medium transition-colors"
                >
                  Clear Filters
                </button>
              )}
            </div>
            
            <div className="flex items-center gap-2 bg-apple-700/40 rounded-xl p-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-lg transition-all duration-200 ${
                  viewMode === 'grid' 
                    ? 'bg-sortio-primary text-white' 
                    : 'text-apple-400 hover:text-apple-50'
                }`}
              >
                <Grid size={18} />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-lg transition-all duration-200 ${
                  viewMode === 'list' 
                    ? 'bg-sortio-primary text-white' 
                    : 'text-apple-400 hover:text-apple-50'
                }`}
              >
                <List size={18} />
              </button>
            </div>
          </div>
        </div>

        {/* Alphabet Navigation */}
        <div className="mb-12">
          <h3 className="text-lg font-medium text-apple-50 mb-4">Browse by Letter</h3>
          <div className="flex flex-wrap gap-2">
            {alphabetIndex.map(({ letter, hasTerms, count }) => (
              <button
                key={letter}
                onClick={() => hasTerms && handleLetterClick(letter)}
                disabled={!hasTerms}
                className={`w-10 h-10 rounded-lg font-medium transition-all duration-200 ${
                  hasTerms
                    ? selectedLetter === letter
                      ? 'bg-sortio-primary text-white'
                      : 'bg-apple-700/40 text-apple-50 hover:bg-apple-600/40 hover:text-sortio-primary'
                    : 'bg-apple-700/20 text-apple-400 cursor-not-allowed'
                }`}
                title={hasTerms ? `${count} terms starting with ${letter}` : `No terms starting with ${letter}`}
              >
                {letter}
              </button>
            ))}
          </div>
        </div>

        {/* Terms Display */}
        {filteredTerms.length === 0 ? (
          <div className="text-center py-16">
            <BookOpen className="mx-auto mb-4 text-apple-400" size={48} />
            <h3 className="text-xl font-medium text-apple-50 mb-2">No terms found</h3>
            <p className="text-apple-400">Try adjusting your search or filter criteria</p>
          </div>
        ) : (
          <div className={
            viewMode === 'grid' 
              ? 'grid md:grid-cols-2 lg:grid-cols-3 gap-6'
              : 'space-y-4'
          }>
            {filteredTerms.map((term) => (
              <Link
                key={term.id}
                to={`/glossary/${term.slug}`}
                className={`group block ${
                  viewMode === 'grid'
                    ? 'bg-apple-700/40 backdrop-blur-md p-6 rounded-2xl border border-apple-600/30 hover:border-sortio-primary/50 transition-all duration-300 hover:translate-y-[-4px]'
                    : 'bg-apple-700/40 backdrop-blur-md p-4 rounded-xl border border-apple-600/30 hover:border-sortio-primary/50 transition-all duration-300 flex items-center justify-between'
                }`}
              >
                <div className={viewMode === 'grid' ? '' : 'flex-1'}>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-medium text-apple-50 group-hover:text-sortio-primary transition-colors">
                      {term.term}
                    </h3>
                    {viewMode === 'grid' && (
                      <ArrowRight className="text-apple-400 group-hover:text-sortio-primary transition-colors" size={16} />
                    )}
                  </div>
                  <div className="mb-3">
                    <span className="inline-block px-2 py-1 bg-sortio-primary/10 text-sortio-primary text-xs font-medium rounded-lg">
                      {term.category}
                    </span>
                  </div>
                  <p className="text-apple-300 text-sm leading-relaxed">
                    {term.shortDescription}
                  </p>
                </div>
                {viewMode === 'list' && (
                  <ArrowRight className="text-apple-400 group-hover:text-sortio-primary transition-colors ml-4" size={16} />
                )}
              </Link>
            ))}
          </div>
        )}

        {/* CTA Section */}
        <div className="mt-24 text-center">
          <div className="bg-gradient-to-r from-sortio-primary/10 to-sortio-secondary/10 rounded-3xl p-8 border border-sortio-primary/20">
            <Sparkles className="mx-auto mb-4 text-sortio-primary" size={32} />
            <h3 className="text-2xl font-semibold text-apple-50 mb-4">
              Ready to Organize Your Files?
            </h3>
            <p className="text-apple-300 mb-6 max-w-2xl mx-auto">
              Put these file organization concepts into practice with Sortio's AI-powered file management system.
            </p>
            <Link
              to="/download"
              className="inline-flex items-center px-8 py-3 bg-sortio-primary hover:bg-sortio-primary/90 text-white font-medium rounded-xl transition-colors duration-200"
            >
              Try Sortio Free
              <ArrowRight className="ml-2" size={16} />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GlossaryPage;